import React from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Separator } from './ui/separator';
import SEO from './SEO';
import { 
  Headphones,
  Phone,
  Calendar,
  Users,
  TrendingUp,
  Zap,
  ArrowRight,
  CheckCircle,
  Globe,
  Clock,
  Brain,
  Target,
  Shield,
  Star,
  MessageCircle,
  Github,
  Twitter,
  Linkedin,
  Youtube
} from 'lucide-react';

const VioSuiteHomepage = () => {
  const handleContact = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  // VioSuite product data
  const vioSuiteProducts = [
    {
      name: "VioQbase",
      tagline: "Voice-Powered Knowledge Base",
      description: "Transform workplace efficiency with voice-activated AI knowledge base. Get instant answers from HR policies, SOPs, and internal docs.",
      icon: Headphones,
      gradient: "from-blue-500/20 to-blue-500/5",
      iconColor: "text-blue-600",
      link: "/products/vioqbase",
      features: ["Voice-Activated Queries", "Enterprise Knowledge Base", "Instant Policy Answers"]
    },
    {
      name: "VioReceptionist", 
      tagline: "AI Voice Receptionist",
      description: "Never miss a call with your 24/7 AI-powered virtual receptionist. Professional, intelligent, always available.",
      icon: Phone,
      gradient: "from-green-500/20 to-green-500/5", 
      iconColor: "text-green-600",
      link: "/products/vioreceptionist",
      features: ["24/7 Call Handling", "Professional Greetings", "Appointment Booking"]
    },
    {
      name: "VioConcierge",
      tagline: "Appointment Management",
      description: "Reduce no-shows by 60-80% with intelligent voice appointment confirmations and automated scheduling.",
      icon: Calendar,
      gradient: "from-purple-500/20 to-purple-500/5",
      iconColor: "text-purple-600", 
      link: "/products/vioconcierge",
      features: ["Appointment Confirmations", "Automated Follow-ups", "Calendar Integration"]
    },
    {
      name: "VioCoach",
      tagline: "Executive Coaching Platform",
      description: "AI-powered coaching platform for professional development and leadership excellence in the modern workplace.",
      icon: Users,
      gradient: "from-orange-500/20 to-orange-500/5",
      iconColor: "text-orange-600",
      link: "/products/viocoach", 
      features: ["Personalized Coaching", "Leadership Development", "Professional Growth"]
    }
  ];

  // Voice AI Benefits
  const voiceAIBenefits = [
    {
      icon: Zap,
      title: "Seamless Interactions",
      description: "Natural, human-like conversations that feel effortless and intuitive for every user interaction."
    },
    {
      icon: Clock,
      title: "Automate Repetitive Tasks", 
      description: "Free your team from routine work by automating repetitive processes and communications."
    },
    {
      icon: TrendingUp,
      title: "Greater Efficiency",
      description: "Streamline complex workflows and eliminate delays that slow down your business operations."
    },
    {
      icon: Target,
      title: "Deeper Engagement",
      description: "Create meaningful connections with customers and employees through intelligent voice interactions."
    },
    {
      icon: Brain,
      title: "Smarter Insights",
      description: "Gain actionable intelligence from every conversation to continuously improve your processes."
    },
    {
      icon: Globe,
      title: "Future-Ready Workplace",
      description: "Build a workplace powered by intelligent communication that adapts to tomorrow's challenges."
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEO 
        title="VioSuite Voice Applications - AI-Powered Voice & Knowledge Solutions"
        description="Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern businesses. Discover VioQbase, VioReceptionist, VioConcierge, and VioCoach."
        keywords="VioSuite, voice AI, VioQbase, VioReceptionist, VioConcierge, VioCoach, business automation, voice technology"
        canonical="https://vioqbase.com"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        
        {/* Audio Waveform Background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-20">
          <div className="flex items-end space-x-1 h-32">
            {/* Audio waveform bars */}
            {Array.from({ length: 60 }).map((_, index) => (
              <div 
                key={index}
                className="bg-[#ff7a1f] rounded-sm animate-pulse"
                style={{
                  width: '4px',
                  height: `${Math.random() * 80 + 20}%`,
                  animationDelay: `${index * 50}ms`
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="relative container mx-auto px-6 py-20 lg:py-32">
          <div className="max-w-7xl mx-auto">
            
            {/* Main Heading */}
            <div className="text-center mb-16">
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight mb-6 text-white">
                Welcome to <span className="text-[#ff7a1f]">Smart AI Solutions</span>
              </h1>
              <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-12">
                The home of VioSuite – a powerful range of Voice AI solutions.
              </h2>
            </div>

            {/* Key Value Propositions in Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              
              {/* Natural Conversations */}
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-16 h-16 bg-[#ff7a1f] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Natural Conversations</h3>
                <p className="text-white leading-relaxed">
                  Bring human-like conversations into the workplace, helping organisations work smarter, faster, and more efficiently.
                </p>
              </div>

              {/* Free Teams from Repetitive Tasks */}
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-16 h-16 bg-[#ff7a1f] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Automate & Streamline</h3>
                <p className="text-white leading-relaxed">
                  Free teams from repetitive tasks and create space for meaningful work by automating processes and streamlining complex workflows.
                </p>
              </div>

              {/* Future-Ready Business */}
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-16 h-16 bg-[#ff7a1f] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Future-Ready Business</h3>
                <p className="text-white leading-relaxed">
                  Transform communication, enhance productivity, and build a future-ready business powered by intelligent voice.
                </p>
              </div>

            </div>

            {/* Central Call to Action */}
            <div className="text-center">
              <p className="text-2xl lg:text-3xl font-semibold text-white mb-8">
                From engaging customers to supporting employees — discover the VioSuite difference.
              </p>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-[#ff7a1f] text-[#ff7a1f] hover:bg-[#ff7a1f] hover:text-white font-semibold px-10 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105"
                onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
              >
                <ArrowRight className="mr-3 h-6 w-6" />
                Explore VioSuite Applications
              </Button>
            </div>

          </div>
        </div>
      </section>

      {/* Products Showcase Section */}
      <section id="products" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Our <span className="text-[#ff7a1f]">Voice AI</span> Solutions Suite
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Comprehensive AI-powered voice solutions designed to transform how your business operates, communicates, and grows.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {vioSuiteProducts.map((product, index) => {
                const IconComponent = product.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 group bg-gradient-to-br from-white to-gray-50">
                    <CardHeader className="pb-6">
                      <div className={`w-20 h-20 bg-gradient-to-br ${product.gradient} rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className={`h-10 w-10 ${product.iconColor}`} />
                      </div>
                      <CardTitle className="text-3xl font-bold text-[#112d56] text-center mb-2">{product.name}</CardTitle>
                      <div className="text-xl font-semibold text-[#ff7a1f] text-center mb-4">{product.tagline}</div>
                      <CardDescription className="text-gray-600 text-center leading-relaxed text-lg mb-6">
                        {product.description}
                      </CardDescription>
                      
                      <div className="space-y-3 mb-6">
                        {product.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                            <span className="text-gray-700 font-medium">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="text-center">
                        <Button 
                          className="bg-[#112d56] hover:bg-[#1a3d6b] text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 group-hover:scale-105"
                          onClick={() => window.location.href = product.link}
                        >
                          Learn More About {product.name}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Voice AI Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Why Companies Should Embrace <span className="text-[#ff7a1f]">Voice AI</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-5xl mx-auto">
                In today's fast-paced world, businesses can't afford delays, missed opportunities, or inconsistent communication. Voice AI changes the way organisations operate by bringing natural, human-like conversations into everyday processes.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 mb-16">
              <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto">
                Whether it's engaging customers, supporting employees, managing schedules, or guiding personal development, Voice AI creates seamless interactions that feel effortless. By automating the repetitive and streamlining the complex, it frees teams to focus on high-value work while ensuring every interaction is captured, handled, and optimised.
              </p>
              <p className="text-xl font-semibold text-[#112d56] text-center mt-6">
                The result is greater efficiency, deeper engagement, smarter insights, and a future-ready workplace powered by intelligent communication.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {voiceAIBenefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="h-8 w-8 text-[#ff7a1f]" />
                      </div>
                      <CardTitle className="text-xl font-bold text-[#112d56] mb-3">{benefit.title}</CardTitle>
                      <CardDescription className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Proven Impact of Voice AI Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Proven Impact of <span className="text-[#ff7a1f]">Voice AI</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Voice AI isn't just the future — it's delivering results right now. Companies using intelligent voice solutions are cutting costs and working smarter.
              </p>
            </div>

            {/* Statistics Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              
              {/* Call Handling Cost Reduction */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                <CardHeader className="pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <TrendingUp className="h-10 w-10 text-green-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">30%</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Cost Reduction</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Call handling costs reduced through intelligent voice automation and streamlined processes.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Resolution Time Improvement */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                <CardHeader className="pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <Clock className="h-10 w-10 text-blue-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">35%</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Faster Resolution</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Resolution times improved with instant voice responses and automated query handling.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Customer Satisfaction */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                <CardHeader className="pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <Star className="h-10 w-10 text-[#ff7a1f]" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">93%</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Customer Satisfaction</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Customers report high satisfaction when engaging with intelligent voice assistants.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* ROI Impact */}
              <Card className="bg-gradient-to-br from-white to-gray-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                <CardHeader className="pb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <Target className="h-10 w-10 text-purple-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">3.5x</div>
                  <CardTitle className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Return on Investment</CardTitle>
                  <CardDescription className="text-gray-600 text-sm">
                    Average ROI for every £1 invested, with 76% of businesses seeing measurable benefits.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>

            {/* Impact Summary */}
            <div className="text-center bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white p-8 rounded-2xl">
              <p className="text-2xl font-semibold mb-4">
                For businesses, the impact is clear: 76% see measurable benefits.
              </p>
              <p className="text-xl text-blue-100">
                With results like these, Voice AI is no longer optional — it's a <span className="text-[#ff7a1f] font-semibold">competitive advantage</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#112d56] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Ready to Transform Your Business with a <span className="text-[#ff7a1f]">VioSuite solution</span>?
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed max-w-4xl mx-auto mb-8">
              Discover how VioSuite Voice Applications can streamline your operations, enhance customer experiences, and drive unprecedented efficiency across your organization.
            </p>
            
            <div className="flex justify-center">
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#112d56] font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300"
                onClick={() => window.location.href = '/contact'}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Contact Smart AI Solutions
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                AI-powered voice & knowledge solutions for modern businesses. Driving efficiency and customer excellence.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/in/smataisolutionstech/" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <Linkedin className="h-6 w-6" />
                </a>
                <a href="https://www.youtube.com/channel/UCij66Z3ffQtVIw3aIqNVN0g" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <Youtube className="h-6 w-6" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">VioSuite Products</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/products/vioqbase" className="hover:text-[#ff7a1f] transition-colors text-lg">VioQbase</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/vioconcierge" className="hover:text-[#ff7a1f] transition-colors text-lg">VioConcierge</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Solutions</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/hr-consultancy" className="hover:text-[#ff7a1f] transition-colors text-lg">HR Consultancy</a></li>
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="text-center">
            <p className="text-blue-200 text-lg">
              © 2025 Smart AI Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VioSuiteHomepage;